import React, { createContext, useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { useAsync } from '../hooks';
import * as auth from '../auth';
import LoadingPage from '../components/LoadingPage';
import ErrorPage from '../components/ErrorPage';
import { User } from '../types/User.type';
import { LoginUserModel } from '../types/LoginUserModel.type';

interface IAuthContext {
  user: User | null;
  getCurrentUser: () => Promise<User | null>;
  login: ({ userIdentifier, password }: LoginUserModel) => Promise<void>;
  logout: () => Promise<void>;
  refreshCurrentUser: () => Promise<void>;
  isVerified: boolean;
  setVerified: (verified: boolean) => void;
}

export const AuthContext = createContext<IAuthContext | undefined>(undefined);
AuthContext.displayName = 'AuthContext';

export function AuthProvider(props: { children: React.ReactNode }): JSX.Element {
  const { data: user, isError, isSuccess, run, setData } = useAsync<User | null>();
  const [isVerified, setIsVerified] = React.useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    run(auth.getCurrentUser());
  }, [run]);

  function setVerified(verified: boolean) {
    setIsVerified(verified);
    // Optional: store in local storage
    localStorage.setItem('verified', verified ? 'true' : 'false');
  }

  useEffect(() => {
    const storedVerified = localStorage.getItem('verified');
    setIsVerified(storedVerified === 'true');
  }, []);

  function refreshCurrentUser(): Promise<void> {
    return run(auth.getCurrentUser()).then(data => {
      // console.log('@@@ refreshCurrentUser', data);
      setData(data); // FIX: Use the newly fetched data
    });
  }

  function login({ userIdentifier, password }: LoginUserModel) {
    return (
      auth
        .login({ userIdentifier, password })
        // @ts-ignore
        .then(u => {
          // @ts-ignore
          setData(u);
        })
    );
  }

  function logout() {
    return auth.logout().then(() => {
      queryClient.clear();
      setVerified(false);
      return setData(null);
    });
  }

  const getCurrentUser = async () => {
    return auth.getCurrentUser();
  };

  if (isSuccess) {
    return (
      <AuthContext.Provider
        value={{
          user,
          getCurrentUser,
          login,
          logout,
          refreshCurrentUser,
          isVerified,
          setVerified,
        }}
        {...props}
      />
    );
  }

  if (isError) {
    return <ErrorPage />;
  }

  return <LoadingPage />;
}

export function useAuth(): IAuthContext {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error('AuthContext should be rendered within an AuthProvider');
  }
  return context;
}
